import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography, useMediaQuery } from "@mui/material";
import { useParams } from "react-router-dom";
import {
  GetProjectsResponse,
  GetShareAccessTokenResponse,
  Project,
  Share,
} from "../../proto/api_pb";
import {
  getShareAccessToken,
  increaseShareUrlViews,
} from "../../services/shareService";
import { getAllProjects } from "../../services/projectService";
import { ContainerForGrid } from "../../components/Exolore/styles";
import { SingleProject } from "../../components/SingleProject/SingleProject";
import { styles } from "../Projects/styles";
import { RpcError } from "grpc-web";
import { toast } from "react-toastify";
export const SharedProjectsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [projects, setProjects] = useState<Project[]>([]);
  const [share, setShare] = useState<Share>();
  const [, setShareToken] = useState<string>();
  const [, setAllowDownload] = useState<boolean>(false);
  const [isMaxViewsReached, setIsMaxViewsReached] = useState<boolean>(false);
  const [isPasswordRequired, setIsPasswordRequired] = useState<boolean>(false);
  const [passwordInput, setPasswordInput] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);
  const { id } = useParams();
  const isMobileScreen = useMediaQuery("(max-width:900px)");
  const [session] = useState<any>(null);
  const [, setInbox] = useState<any>(null);

  const handleShareResponse = async (response: any) => {
    if (response instanceof GetShareAccessTokenResponse) {
      const shareData = response.getShare();
      if (shareData) {
        setShare(shareData);
        setShareToken(response.getAccessToken());
        setAllowDownload(shareData.getAllowDownload());

        if (
          !shareData.getUnlimitedViews() &&
          shareData.getImpressionCount() >= shareData.getMaxViews()
        ) {
          setIsMaxViewsReached(true);
          toast.error("This share link has reached its maximum view limit");
          return;
        }

        await increaseShareUrlViews(id!, response.getAccessToken());
        await fetchProjects(response.getAccessToken(), shareData);
      }
    }
  };

  useEffect(() => {
    const fetchShareData = async () => {
      if (!id) return;

      try {
        const response = await getShareAccessToken(id);
        await handleShareResponse(response);
      } catch (error: any) {
        const errorMessage = error?.message || "";
        if (errorMessage.includes("password required")) {
          setIsPasswordRequired(true);
        } else {
          console.error("Error fetching share data:", error);
          toast.error("Failed to load shared projects");
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchShareData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handlePasswordSubmit = async () => {
    if (!id || !passwordInput.trim()) return;
    setPasswordLoading(true);
    setPasswordError("");

    try {
      const response = await getShareAccessToken(id, passwordInput.trim());
      await handleShareResponse(response);
      setIsPasswordRequired(false);
    } catch (error: any) {
      const errorMessage = error?.message || "";
      if (errorMessage.includes("incorrect password")) {
        setPasswordError("Incorrect password");
      } else if (errorMessage.includes("password required")) {
        setPasswordError("Password is required");
      } else {
        setPasswordError("Failed to access shared projects");
      }
    } finally {
      setPasswordLoading(false);
    }
  };

  const fetchProjects = async (token: string, shareData: Share) => {
    const shareType = shareData.getShareType() as number;
    const objectId = shareData.getObjectId();

    const isProjectsCollection = shareType === 5 || (objectId && objectId.includes(","));

    if (!isProjectsCollection) {
      toast.error("Invalid share type");
      return;
    }

    const projectIds = shareData.getObjectId().split(",").map(id => id.trim()).filter(Boolean);

    if (projectIds.length === 0) {
      toast.error("No projects found in share");
      return;
    }

    try {
      const enterpriseId = shareData.getEnterpriseId?.() || "";

      const response = await getAllProjects(
        token,
        shareData.getOwnerId(),
        "",
        undefined,
        enterpriseId,
        0,
        2, // ROLE_ADMIN - bypass collaborator filter for shared projects
        "",
        ""
      );

      if (response instanceof GetProjectsResponse) {
        const allProjects = response.getProjectsList();
        const filteredProjects = allProjects.filter(project =>
          projectIds.includes(project.getId())
        );
        setProjects(filteredProjects);
      } else if (response instanceof RpcError) {
        console.error("RPC Error:", response);
        toast.error("Failed to load projects");
      }
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isMaxViewsReached) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography>This share link has reached its maximum view limit.</Typography>
      </Box>
    );
  }

  if (isPasswordRequired) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "16px",
            padding: "32px",
            borderRadius: "8px",
            border: "1px solid #333",
            maxWidth: "400px",
            width: "100%",
          }}
        >
          <Typography sx={{ fontWeight: "bold", fontSize: "20px", textAlign: "center" }}>
            This share is password protected
          </Typography>
          <TextField
            type="password"
            placeholder="Enter password"
            value={passwordInput}
            onChange={(e) => setPasswordInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handlePasswordSubmit();
            }}
            error={!!passwordError}
            helperText={passwordError}
            fullWidth
          />
          <Button
            variant="contained"
            onClick={handlePasswordSubmit}
            disabled={passwordLoading || !passwordInput.trim()}
            sx={{ textTransform: "none" }}
          >
            {passwordLoading ? <CircularProgress size={24} /> : "Submit"}
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ height: "100vh", display: "flex" }}>
        <Box sx={styles.rightSideContainer}>
          <Box
            sx={{
              ...styles.projectsHeaderContainer,
              width: isMobileScreen ? "85vw" : "100%",
            }}
          >
            <Box sx={styles.headerContainer}>
              <Typography sx={{ fontWeight: "bold", fontSize: "30px" }}>
                {share?.getTrackTitle() || "Shared Projects"}
              </Typography>
            </Box>
          </Box>
          <ContainerForGrid
            sx={
              isMobileScreen ? { marginBottom: "90px" } : { marginTop: "15px" }
            }
          >
            {projects.map((project, index) => (
              <SingleProject
                key={index}
                index={index}
                project={project}
                session={session}
                setInbox={setInbox}
                handleUseEffect={async () => { }}
                openFoldersList={[]}
                setOpenFoldersList={() => { }}
                isReorderAvailable={false}
                handleDragStart={() => { }}
                handleDrop={() => { }}
                setDraggedIndex={() => { }}
              />
            ))}
          </ContainerForGrid>
          {projects.length === 0 && !isLoading && (
            <Box sx={styles.emptyMessageContainer}>
              <Typography>No projects found!</Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

